import React, { useCallback, useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import GazeHeatmap360Viewer from "../gazeHeatmap/GazeHeatmap360Viewer";
import HeatmapComparisonView from "../gazeHeatmap/HeatmapComparisonView";
import InfoCallout from "../../../../common/components/InfoCallout/InfoCallout";
import type { LearnerGroup } from "../../types/groupTypes";

/** Scene option for navigation dropdown */
interface SceneOption {
  id: number;
  name: string;
  sceneType?: string | null;
}

interface GazeHeatmapModalProps {
  /** Scene name for the title (used when scenes list is not provided) */
  sceneName: string;
  /** Session ID for individual view */
  sessionId?: number;
  /** Content ID (scene) for the heatmap (used when scenes list is not provided) */
  contentId?: number;
  /** Session IDs for aggregated view */
  sessionIds?: number[];
  /** Groups for comparison view */
  comparisonGroups?: LearnerGroup[];
  /** List of available scenes for navigation */
  scenes?: SceneOption[];
  /** Initial scene ID to select */
  initialSceneId?: number;
  /** Close callback */
  onClose: () => void;
}

/**
 * Modal wrapper for the gaze heatmap viewer
 * Supports individual session view, aggregated view, and comparison view
 */
const GazeHeatmapModal: React.FC<GazeHeatmapModalProps> = ({
  sceneName,
  sessionId,
  contentId,
  sessionIds,
  comparisonGroups,
  scenes,
  initialSceneId,
  onClose,
}) => {
  const { t } = useTranslation();

  // Scene navigation state
  const [selectedSceneId, setSelectedSceneId] = useState<number | undefined>(
    initialSceneId || contentId,
  );

  // Help section toggle
  const [showHelp, setShowHelp] = useState(false);

  // Update selected scene if initialSceneId changes
  useEffect(() => {
    if (initialSceneId) {
      setSelectedSceneId(initialSceneId);
    }
  }, [initialSceneId]);

  // Compute effective values based on selected scene
  const currentScene = scenes?.find((s) => s.id === selectedSceneId);
  const effectiveContentId = selectedSceneId || contentId;
  const effectiveSceneName = currentScene?.name || sceneName;

  // Check if we should show the scene dropdown (more than 1 scene available)
  const showSceneDropdown = scenes && scenes.length > 1;

  // Determine mode
  const isComparisonMode = comparisonGroups && comparisonGroups.length >= 2;

  // Handle export - combine background image with heatmap overlay
  const handleExport = useCallback(() => {
    const viewer = document.querySelector(".gaze-heatmap-viewer");
    if (!viewer) return;

    // Find all canvases and check for WebGL canvas (360° view)
    const canvases = Array.from(viewer.querySelectorAll("canvas"));
    const webglCanvas = canvases.find((canvas) => {
      const gl = canvas.getContext("webgl") || canvas.getContext("webgl2");
      return gl !== null;
    });

    if (webglCanvas) {
      // 360° view: export the WebGL canvas directly (already has background + heatmap)
      const dataUrl = webglCanvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.download = `heatmap-${effectiveSceneName.replace(/\s+/g, "_")}.png`;
      link.href = dataUrl;
      link.click();
    } else {
      // Flat view: combine background image with heatmap canvas
      const img = viewer.querySelector("img") as HTMLImageElement | null;
      const heatmapCanvas = canvases[0] as HTMLCanvasElement | null;
      if (!img || !heatmapCanvas) return;

      // Create a new canvas to combine both
      const exportCanvas = document.createElement("canvas");
      const ctx = exportCanvas.getContext("2d");
      if (!ctx) return;

      // Use the heatmap canvas dimensions (which match the equirectangular projection)
      exportCanvas.width = heatmapCanvas.width;
      exportCanvas.height = heatmapCanvas.height;

      // Draw the background image first, scaled to fill
      ctx.drawImage(img, 0, 0, exportCanvas.width, exportCanvas.height);

      // Get the current opacity from the canvas style
      const opacity = parseFloat(heatmapCanvas.style.opacity) || 1.0;

      // Draw the heatmap overlay with opacity
      ctx.globalAlpha = opacity;
      ctx.drawImage(heatmapCanvas, 0, 0);
      ctx.globalAlpha = 1.0;

      // Create download link
      const dataUrl = exportCanvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.download = `heatmap-${effectiveSceneName.replace(/\s+/g, "_")}.png`;
      link.href = dataUrl;
      link.click();
    }
  }, [effectiveSceneName]);

  // Track if heatmap has data (for disabling export button)
  const [hasHeatmapData, setHasHeatmapData] = useState(false);

  // Scene dropdown state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const titleButtonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Handle scene selection from dropdown
  const handleSceneSelect = useCallback((sceneId: number) => {
    setSelectedSceneId(sceneId);
    setIsDropdownOpen(false);
    setHasHeatmapData(false); // Reset when changing scene
  }, []);

  // Handle data load from heatmap viewer
  const handleDataLoad = useCallback((pointCount: number) => {
    setHasHeatmapData(pointCount > 0);
  }, []);

  // Toggle dropdown and calculate position
  const toggleDropdown = useCallback(() => {
    if (!isDropdownOpen && titleButtonRef.current) {
      const rect = titleButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
    setIsDropdownOpen(!isDropdownOpen);
  }, [isDropdownOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isDropdownOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        titleButtonRef.current &&
        !titleButtonRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isDropdownOpen]);

  // Render scene type icon - uses currentColor to inherit from parent
  const renderSceneIcon = (sceneType: string | null | undefined) => {
    if (sceneType === "video360") {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z"
          />
        </svg>
      );
    }
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4 flex-shrink-0"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
    );
  };

  return (
    <div
      className="stats-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="gaze-heatmap-title"
    >
      <div
        className="stats-modal-content w-full max-w-6xl h-[85vh] max-h-[900px] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label={t("general.back")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </button>
            <div>
              {showSceneDropdown ? (
                <button
                  ref={titleButtonRef}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleDropdown();
                  }}
                  className="group flex items-center gap-1.5 text-left hover:bg-gray-50 rounded-md px-1.5 py-0.5 -mx-1.5 -my-0.5 transition-colors"
                >
                  <h2 id="gaze-heatmap-title" className="text-xl font-bold text-gray-900">
                    {effectiveSceneName}
                  </h2>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 text-gray-400 group-hover:text-gray-600 transition-all ${
                      isDropdownOpen ? "rotate-180" : ""
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              ) : (
                <h2 id="gaze-heatmap-title" className="text-xl font-bold text-gray-900">
                  {effectiveSceneName}
                </h2>
              )}
              <p className="text-sm text-gray-500 mt-1">{t("statsNew.gazeHeatmap.title")}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Export button */}
            <button
              onClick={handleExport}
              disabled={!hasHeatmapData}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                hasHeatmapData
                  ? "bg-white border border-gray-300 hover:bg-gray-50 text-gray-700"
                  : "bg-gray-100 border border-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              <span className="text-sm font-medium">{t("statsNew.gazeHeatmap.export")}</span>
            </button>

            {/* Close button */}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label={t("general.close")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Beta notice + Help toggle */}
        <div className="mx-6 mt-4 flex items-start gap-3">
          {/* Beta notice */}
          <div className="flex-1 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="text-sm">
                <span className="font-semibold text-amber-700">BETA</span>
                <span className="text-amber-600">
                  {" — "}
                  {t("statsNew.gazeHeatmap.betaNotice")}
                </span>
              </div>
            </div>
          </div>

          {/* Help toggle button */}
          <button
            onClick={() => setShowHelp(!showHelp)}
            className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border transition-colors ${
              showHelp
                ? "bg-blue-50 border-blue-200 text-blue-700"
                : "bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100"
            }`}
            title={t("statsNew.gazeHeatmap.helpTitle")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-sm font-medium whitespace-nowrap">
              {showHelp ? t("general.hide") : t("general.help")}
            </span>
          </button>
        </div>

        {/* Help section - collapsible */}
        {showHelp && (
          <InfoCallout className="mx-6 mt-3" title={t("statsNew.gazeHeatmap.helpTitle")}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Heatmap explanation */}
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-azure-100 rounded-lg flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 text-azure-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z"
                    />
                  </svg>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-azure-800">
                    {t("statsNew.gazeHeatmap.helpHeatmapTitle")}
                  </h4>
                  <p className="text-xs text-azure-600 mt-1">
                    {t("statsNew.gazeHeatmap.helpHeatmapDesc")}
                  </p>
                </div>
              </div>

              {/* Scanpath explanation */}
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-azure-100 rounded-lg flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 text-azure-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                    />
                  </svg>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-azure-800">
                    {t("statsNew.gazeHeatmap.helpScanpathTitle")}
                  </h4>
                  <p className="text-xs text-azure-600 mt-1">
                    {t("statsNew.gazeHeatmap.helpScanpathDesc")}
                  </p>
                </div>
              </div>

              {/* Combined explanation */}
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-azure-100 rounded-lg flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 text-azure-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                    />
                  </svg>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-azure-800">
                    {t("statsNew.gazeHeatmap.helpBothTitle")}
                  </h4>
                  <p className="text-xs text-azure-600 mt-1">
                    {t("statsNew.gazeHeatmap.helpBothDesc")}
                  </p>
                </div>
              </div>
            </div>
          </InfoCallout>
        )}

        {/* Content */}
        <div className="flex-1 p-6 overflow-y-auto gaze-heatmap-viewer">
          {isComparisonMode && effectiveContentId ? (
            <HeatmapComparisonView
              contentId={effectiveContentId}
              groups={comparisonGroups.map((g) => ({
                id: g.id,
                name: g.name,
                memberIds: g.memberIds,
                color: g.color,
              }))}
              onClose={onClose}
            />
          ) : (
            <GazeHeatmap360Viewer
              key={effectiveContentId}
              // When navigating between scenes in the modal, use aggregated mode
              // (sessionIds instead of sessionId) to avoid the internal scene selector
              sessionId={showSceneDropdown ? undefined : sessionId}
              contentId={effectiveContentId}
              sessionIds={showSceneDropdown && sessionId ? [sessionId] : sessionIds}
              onDataLoad={handleDataLoad}
            />
          )}
        </div>
      </div>

      {/* Scene selection dropdown */}
      {isDropdownOpen &&
        showSceneDropdown &&
        createPortal(
          <div
            ref={dropdownRef}
            onClick={(e) => e.stopPropagation()}
            className="fixed z-[9999] bg-white border border-gray-200 rounded-xl shadow-2xl max-h-80 overflow-hidden min-w-[320px]"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
            }}
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                {t("statsNew.gazeHeatmap.selectScene")}
              </p>
            </div>

            {/* Scrollable scene list */}
            <div className="py-2 max-h-64 overflow-auto">
              {scenes?.map((scene) => {
                const isSelected = scene.id === selectedSceneId;

                return (
                  <button
                    key={scene.id}
                    onClick={() => handleSceneSelect(scene.id)}
                    className={`group flex items-center gap-3 w-full px-4 py-3 text-left transition-all ${
                      isSelected
                        ? "bg-green-50 text-green-700 border-l-2 border-green-500"
                        : "text-gray-700 hover:bg-gray-100 hover:border-l-2 hover:border-gray-300 border-l-2 border-transparent"
                    }`}
                  >
                    {/* Icon with dynamic color */}
                    <span className={isSelected ? "text-green-500" : "text-gray-400"}>
                      {renderSceneIcon(scene.sceneType)}
                    </span>
                    <span className="flex-1 truncate font-medium">{scene.name}</span>
                    {isSelected && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 text-green-600 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default GazeHeatmapModal;
